import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useBreakdownPanel } from "../BreakdownPanelContext";
import { RuleSectionsView } from "./RuleSectionsView";
import type { RuleSubject } from "./ruleCardSubjects";
import { useRuleCardWageSettings } from "./useRuleCardReads";

type Props = {
	departmentId: string;
	/** The rules' department is not the one on screen: the employee's home department. */
	isHomeDepartment: boolean;
	subjects: readonly RuleSubject[];
};

/**
 * Today's rows, read through the same cache entry as that department's Wage Settings tab. Fetched the
 * first time a card opens — the card's body mounts only while it is open — and kept fresh for a while
 * (`useRuleCardReads`). When they cannot be read, the rules show as captured when the shift was priced,
 * and the card says so; a legacy row, which captured nothing, says the read failed.
 */
const LiveRuleSections: FC<Props> = ({ departmentId, isHomeDepartment, subjects }) => {
	const { t } = useTranslation();
	const settings = useRuleCardWageSettings(departmentId);
	if (settings.isPending) return <RuleSectionsView subjects={subjects} notes={[]} isHomeDepartment={isHomeDepartment} />;
	if (settings.isError) {
		const note = t(isHomeDepartment ? "wageBreakdown.card.homeDepartmentAsPriced" : "wageBreakdown.card.loadError");
		return <RuleSectionsView subjects={subjects} source={{ type: "captured" }} notes={[note]} isHomeDepartment={isHomeDepartment} />;
	}
	return (
		<RuleSectionsView
			subjects={subjects}
			source={{ type: "live", settings: settings.data }}
			notes={isHomeDepartment ? [t("wageBreakdown.card.homeDepartment")] : []}
			isHomeDepartment={isHomeDepartment}
		/>
	);
};

/**
 * The rule sections of one department in a card. A viewer who may not open Wage Settings could not read
 * it either (the same permission guards the read), so they see the rules as captured, without a request
 * that would only be refused. A legacy row captured nothing, so for them it has no section at all (its
 * rate type is not given a card either — `RateTypeCell`).
 */
export const DepartmentRuleSections: FC<Props> = (props) => {
	const { t } = useTranslation();
	const { access } = useBreakdownPanel();
	if (access.canOpenWageSettings) return <LiveRuleSections {...props} />;
	const captured = props.subjects.filter((subject) => subject.kind !== "legacy");
	if (captured.length === 0) return null;
	const note = t(props.isHomeDepartment ? "wageBreakdown.card.homeDepartmentAsPriced" : "wageBreakdown.card.asPriced");
	return <RuleSectionsView subjects={captured} source={{ type: "captured" }} notes={[note]} isHomeDepartment={props.isHomeDepartment} />;
};
